export interface CityRecord {
  id: string;
  name: string;
  asciiname: string;
  lat: number;
  lng: number;
  countryCode: string;
  admin1Code: string;
  population: number;
  searchKey: string;
}

function resolveCitySearchBase(): string {
  const configured = import.meta.env.VITE_CITY_SEARCH_BASE_URL?.trim();
  if (configured) {
    return configured.replace(/\/$/, '');
  }

  // Same-origin path proxied to S3 in dev (vite) and prod (vercel.json).
  return `${import.meta.env.BASE_URL.replace(/\/?$/, '/')}city-search`;
}

const CITY_SEARCH_BASE = resolveCitySearchBase();

const VALID_SHARD_KEY = /^(?:__|[a-z]{2}|[a-z]_)$/;

const MISSING_INDEX_MESSAGE =
  'Could not load city search data. Check your connection and try again.';

let manifestPromise: Promise<string[]> | null = null;
const shardCache = new Map<string, CityRecord[]>();
const shardLoadPromises = new Map<string, Promise<CityRecord[]>>();

const regionDisplay =
  typeof Intl !== 'undefined'
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

function normalizeSearchText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .trim();
}

function scoreCityMatch(city: CityRecord, normalizedQuery: string): number {
  const normalizedName = normalizeSearchText(city.name);
  const normalizedAscii = normalizeSearchText(city.asciiname);
  const queryWords = normalizedQuery.split(/\s+/).filter(Boolean);

  if (
    normalizedAscii === normalizedQuery ||
    normalizedName === normalizedQuery
  ) {
    return 1000;
  }

  if (
    normalizedAscii.startsWith(normalizedQuery) ||
    normalizedName.startsWith(normalizedQuery)
  ) {
    return 900;
  }

  if (queryWords.length > 1) {
    const primaryText = `${normalizedName} ${normalizedAscii}`;
    if (queryWords.every((word) => primaryText.includes(word))) {
      return 800;
    }
  }

  if (
    normalizedAscii.includes(normalizedQuery) ||
    normalizedName.includes(normalizedQuery)
  ) {
    return 700;
  }

  if (city.searchKey.includes(normalizedQuery)) {
    return 500;
  }

  if (
    queryWords.length > 1 &&
    queryWords.every((word) => city.searchKey.includes(word))
  ) {
    return 400;
  }

  return 0;
}

function parseCityColumns(
  columns: string[],
  name: string,
  asciiname: string
): Pick<CityRecord, 'countryCode' | 'admin1Code' | 'population' | 'searchKey'> {
  const populationInColumn7 =
    columns.length >= 8 && /^\d+$/.test(columns[7]?.trim() ?? '');

  if (populationInColumn7) {
    return {
      countryCode: columns[5]?.trim() || '',
      admin1Code: columns[6]?.trim() || '',
      population: Number(columns[7]) || 0,
      searchKey:
        columns[8]?.trim() ||
        normalizeSearchText(`${name} ${asciiname}`),
    };
  }

  return {
    countryCode: columns[5]?.trim() || '',
    admin1Code: '',
    population: Number(columns[6]) || 0,
    searchKey:
      columns[7]?.trim() || normalizeSearchText(`${name} ${asciiname}`),
  };
}

function parseShardCatalog(raw: string): CityRecord[] {
  const citiesById = new Map<string, CityRecord>();

  for (const line of raw.split('\n')) {
    if (!line) continue;

    const columns = line.split('\t');
    if (columns.length < 7) continue;

    const lat = Number(columns[3]);
    const lng = Number(columns[4]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;

    const asciiname = columns[2]?.trim() || columns[1]?.trim();
    const name = columns[1]?.trim() || asciiname;
    if (!asciiname) continue;

    const { countryCode, admin1Code, population, searchKey } = parseCityColumns(
      columns,
      name,
      asciiname
    );

    citiesById.set(columns[0], {
      id: columns[0],
      name,
      asciiname,
      lat,
      lng,
      countryCode,
      admin1Code,
      population,
      searchKey,
    });
  }

  return Array.from(citiesById.values());
}

function formatAdmin1Label(admin1Code: string): string | null {
  if (!admin1Code) return null;
  if (/^[A-Za-z]{2,3}$/.test(admin1Code)) {
    return admin1Code.toUpperCase();
  }
  return null;
}

function citySearchUrl(path: string): string {
  return `${CITY_SEARCH_BASE}/${path}`;
}

async function readCitySearchResponse(
  response: Response,
  resourceLabel: string
): Promise<string> {
  if (!response.ok) {
    throw new Error(MISSING_INDEX_MESSAGE);
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('text/html')) {
    throw new Error(MISSING_INDEX_MESSAGE);
  }

  const body = await response.text();
  if (body.trimStart().startsWith('<!doctype') || body.trimStart().startsWith('<!DOCTYPE')) {
    throw new Error(MISSING_INDEX_MESSAGE);
  }

  if (resourceLabel.endsWith('.json')) {
    try {
      JSON.parse(body);
    } catch {
      throw new Error(MISSING_INDEX_MESSAGE);
    }
  }

  return body;
}

async function loadManifestShards(): Promise<string[]> {
  if (!manifestPromise) {
    manifestPromise = fetch(citySearchUrl('manifest.json'))
      .then(async (response) => {
        const body = await readCitySearchResponse(response, 'manifest.json');
        const manifest = JSON.parse(body) as { shards?: string[] };
        if (!Array.isArray(manifest.shards) || manifest.shards.length === 0) {
          throw new Error('City search index manifest is empty.');
        }

        const shards = manifest.shards.filter(
          (shard): shard is string =>
            typeof shard === 'string' && VALID_SHARD_KEY.test(shard)
        );
        if (shards.length === 0) {
          throw new Error('City search index manifest has no valid shards.');
        }

        return shards;
      })
      .catch((error) => {
        manifestPromise = null;
        throw error;
      });
  }

  return manifestPromise;
}

function shardKeysForQuery(normalizedQuery: string, availableShards: string[]): string[] {
  if (normalizedQuery.length >= 2) {
    const shard = normalizedQuery.slice(0, 2);
    return availableShards.includes(shard) ? [shard] : [];
  }

  const prefix = normalizedQuery.slice(0, 1);
  return availableShards.filter(
    (shard) => shard.startsWith(prefix) || shard === `${prefix}_`
  );
}

async function loadShard(shard: string): Promise<CityRecord[]> {
  if (!VALID_SHARD_KEY.test(shard)) {
    throw new Error('Invalid city search shard.');
  }

  const cached = shardCache.get(shard);
  if (cached) {
    return cached;
  }

  const inFlight = shardLoadPromises.get(shard);
  if (inFlight) {
    return inFlight;
  }

  const loadPromise = fetch(citySearchUrl(`${shard}.tsv`))
    .then(async (response) => {
      const body = await readCitySearchResponse(response, `${shard}.tsv`);
      const records = parseShardCatalog(body);
      shardCache.set(shard, records);
      return records;
    })
    .finally(() => {
      shardLoadPromises.delete(shard);
    });

  shardLoadPromises.set(shard, loadPromise);
  return loadPromise;
}

export async function ensureCitySearchReady(): Promise<void> {
  await loadManifestShards();
}

export async function searchCitiesInCatalog(
  query: string,
  limit = 8
): Promise<CityRecord[]> {
  const normalizedQuery = normalizeSearchText(query);
  if (normalizedQuery.length < 2) {
    return [];
  }

  const availableShards = await loadManifestShards();
  const shardKeys = shardKeysForQuery(normalizedQuery, availableShards);
  if (shardKeys.length === 0) {
    return [];
  }

  const shardCatalogs = await Promise.all(shardKeys.map(loadShard));
  const catalog = shardCatalogs.flat();

  return searchCities(catalog, query, limit);
}

export function formatCityLabel(
  city: Pick<CityRecord, 'asciiname' | 'countryCode' | 'admin1Code'>
): string {
  const country =
    (city.countryCode && regionDisplay?.of(city.countryCode)) ||
    city.countryCode ||
    '';
  const admin1 = formatAdmin1Label(city.admin1Code);
  const parts = [city.asciiname];
  if (admin1) parts.push(admin1);
  if (country) parts.push(country);
  return parts.join(', ');
}

export function searchCities(
  catalog: CityRecord[],
  query: string,
  limit = 8
): CityRecord[] {
  const normalizedQuery = normalizeSearchText(query);
  if (normalizedQuery.length < 2) {
    return [];
  }

  const matchesById = new Map<string, { city: CityRecord; score: number }>();
  for (const city of catalog) {
    const score = scoreCityMatch(city, normalizedQuery);
    if (score === 0) continue;

    const existing = matchesById.get(city.id);
    if (
      !existing ||
      score > existing.score ||
      (score === existing.score && city.population > existing.city.population)
    ) {
      matchesById.set(city.id, { city, score });
    }
  }

  const seenLabels = new Set<string>();
  const results: CityRecord[] = [];

  for (const { city } of [...matchesById.values()].sort(
    (left, right) =>
      right.score - left.score ||
      right.city.population - left.city.population
  )) {
    const label = formatCityLabel(city);
    if (seenLabels.has(label)) continue;
    seenLabels.add(label);
    results.push(city);
    if (results.length >= limit) break;
  }

  return results;
}

export function cityRecordToBirthPlace(city: CityRecord) {
  return {
    label: formatCityLabel(city),
    placeId: `geoname:${city.id}`,
    lat: city.lat,
    lng: city.lng,
  };
}
